import Quill from "quill";
import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  FloatingLabel,
  Alert,
  Row,
  Col,
  Card,
  ProgressBar,
} from "react-bootstrap";
import {
  IoClose,
  IoCloudUpload,
  IoTrash,
  IoDocumentText,
  IoPlayCircle,
  IoNewspaper,
  IoArrowBack,
  IoPerson,
  IoBusiness,
  IoAdd,
} from "react-icons/io5";

const ArtikelModal = ({ show, handleClose, handleSubmit, initialData }) => {
  const [contentType, setContentType] = useState(null);

  const [formData, setFormData] = useState({
    judul: "",
    type_konten: null,
    type_artikel: null,
    ringkasan: "",
    konten: "",
    url_youtube: "",
    file_majalah: null,
    file_majalah_base64: "",
    status: "DRAFT",
    thumbnail: "",
    thumbnailFile: null,
    allowComments: true,
    isFeatured: false,
    file: [],
    category: null, // Added for Fasilitas & Layanan
  });

  const editorRef = useRef(null);
  const quillInstanceRef = useRef(null);
  const fileInputRef = useRef(null);
  const majalahFileInputRef = useRef(null);
  const multipleFileInputRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingMajalah, setIsUploadingMajalah] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [majalahUploadProgress, setMajalahUploadProgress] = useState(0);
  const [multipleFileUploadProgress, setMultipleFileUploadProgress] = useState(
    {},
  );

  const quillTypes = ["ARTIKEL", "COE", "FASILITAS_LAYANAN"];
  // Types that support multiple file uploads (Podcast and Majalah do NOT support them)
  const fileSupportTypes = ["ARTIKEL", "COE", "FASILITAS_LAYANAN"];

  const isEmptyContent = (html) => {
    if (!html) return true;
    const div = document.createElement("div");
    div.innerHTML = html;
    const text = div.textContent || div.innerText || "";
    return text.trim() === "";
  };

  const formatExistingFiles = (files) => {
    if (!files || !Array.isArray(files)) return [];
    return files.map((f, idx) => ({
      file: f.file,
      name: f.name || `File ${idx + 1}`,
      id: `existing-${idx}-${Date.now()}-${Math.random()}`,
      size: f.size || Math.round((f.file.length * 3) / 4),
      isExisting: true,
    }));
  };

  // Quill initialization
  useEffect(() => {
    if (!show || !quillTypes.includes(contentType) || !editorRef.current) {
      if (quillInstanceRef.current) {
        if (editorRef.current) {
          while (editorRef.current.firstChild) {
            editorRef.current.removeChild(editorRef.current.firstChild);
          }
        }
        quillInstanceRef.current = null;
      }
      return;
    }

    while (editorRef.current.firstChild) {
      editorRef.current.removeChild(editorRef.current.firstChild);
    }

    const editorContainer = document.createElement("div");
    editorContainer.style.height = "200px";
    editorContainer.style.border = "1px solid #ccc";
    editorContainer.style.borderRadius = "4px";
    editorRef.current.appendChild(editorContainer);

    const quill = new Quill(editorContainer, {
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline"],
          ["blockquote", "code-block"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["link", "image"],
          ["clean"],
        ],
      },
      theme: "snow",
      placeholder: "Konten...",
    });

    if (formData.konten) {
      quill.root.innerHTML = formData.konten;
    }

    const handleTextChange = () => {
      const content = quill.root.innerHTML;
      setFormData((prev) => ({ ...prev, konten: content }));
    };

    quill.on("text-change", handleTextChange);
    quillInstanceRef.current = quill;

    return () => {
      quill.off("text-change", handleTextChange);
      if (editorRef.current) {
        while (editorRef.current.firstChild) {
          editorRef.current.removeChild(editorRef.current.firstChild);
        }
      }
      if (quillInstanceRef.current === quill) {
        quillInstanceRef.current = null;
      }
    };
  }, [show, contentType]);

  useEffect(() => {
    if (
      quillTypes.includes(contentType) &&
      quillInstanceRef.current &&
      formData.konten
    ) {
      const currentContent = quillInstanceRef.current.root.innerHTML;
      if (currentContent !== formData.konten) {
        quillInstanceRef.current.root.innerHTML = formData.konten;
      }
    }
  }, [formData.konten, contentType]);

  useEffect(() => {
    if (show) {
      if (initialData) {
        setContentType(initialData.type_konten || "ARTIKEL");
        setFormData({
          judul: initialData.judul || "",
          type_konten: initialData.type_konten || null,
          type_artikel: initialData.type_artikel || null,
          ringkasan: initialData.ringkasan || "",
          konten: initialData.konten || "",
          file_majalah: initialData.file_majalah || null,
          file_majalah_base64: initialData.file_majalah_base64 || "",
          status: initialData.status || "DRAFT",
          thumbnail: initialData.thumbnail || "",
          thumbnailFile: null,
          allowComments: initialData.allowComments !== false,
          isFeatured: initialData.isFeatured || false,
          url_youtube: initialData.url_youtube || "",
          file: formatExistingFiles(initialData.file),
          category: initialData.category || null, // Added
        });
      } else {
        setContentType(null);
        setFormData({
          judul: "",
          type_konten: null,
          type_artikel: null,
          ringkasan: "",
          konten: "",
          url_youtube: "",
          file_majalah: null,
          file_majalah_base64: "",
          status: "DRAFT",
          thumbnail: "",
          thumbnailFile: null,
          allowComments: true,
          isFeatured: false,
          file: [],
          category: null, // Added
        });
      }
      setErrors({});
      setIsUploading(false);
      setIsUploadingMajalah(false);
      setUploadProgress(0);
      setMajalahUploadProgress(0);
      setMultipleFileUploadProgress({});
    }
  }, [show, initialData]);

  const handleSelectContentType = (type) => {
    setContentType(type);
    setFormData((prev) => ({
      ...prev,
      type_konten: type,
      ...(type !== "ARTIKEL" && { type_artikel: null }),
      ...(type !== "PODCAST" && { url_youtube: "" }),
      ...(type !== "MAJALAH" && {
        file_majalah: null,
        file_majalah_base64: "",
      }),
      ...(type !== "FASILITAS_LAYANAN" && { category: null }), // Reset category if not Fasilitas & Layanan
      konten: "",
      // Clear multiple files when switching to types that don't support them
      ...((type === "PODCAST" || type === "MAJALAH") && { file: [] }),
    }));
  };

  const handleBackToTypeSelection = () => {
    setContentType(null);
    setErrors({});
  };

  const getQuillContent = () => {
    if (quillTypes.includes(contentType) && quillInstanceRef.current) {
      return quillInstanceRef.current.root.innerHTML;
    }
    return formData.konten;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Thumbnail handlers
  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        thumbnail: "File harus berupa gambar (JPEG, PNG, GIF, WebP)",
      }));
      return;
    }
    if (file.size > maxSize) {
      setErrors((prev) => ({ ...prev, thumbnail: "Ukuran file maksimal 5MB" }));
      return;
    }

    setFormData((prev) => ({ ...prev, thumbnailFile: file, thumbnail: "" }));
    convertToBase64(file, "thumbnail");
  };

  const handleRemoveThumbnail = () => {
    setFormData((prev) => ({ ...prev, thumbnail: "", thumbnailFile: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Majalah file handlers
  const handleMajalahFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const maxSize = 20 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        file_majalah: "File harus berupa PDF atau Word (DOC/DOCX)",
      }));
      return;
    }
    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        file_majalah: "Ukuran file maksimal 20MB",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      file_majalah: file,
      file_majalah_base64: "",
    }));
    convertToBase64(file, "majalah");
  };

  const handleRemoveMajalahFile = () => {
    setFormData((prev) => ({
      ...prev,
      file_majalah: null,
      file_majalah_base64: "",
    }));
    if (majalahFileInputRef.current) majalahFileInputRef.current.value = "";
  };

  // Multiple files handlers (only used when contentType supports files)
  const handleMultipleFilesSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    files.forEach((file) => {
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const maxSize = 20 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          multipleFiles: `File ${file.name} harus berupa PDF, Word, atau gambar (JPEG, PNG, GIF, WebP)`,
        }));
        return;
      }
      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          multipleFiles: `File ${file.name} melebihi 20MB`,
        }));
        return;
      }
      convertMultipleFileToBase64(file);
    });
    e.target.value = "";
  };

  const convertMultipleFileToBase64 = (file) => {
    const fileId = Date.now() + "-" + Math.random();
    setMultipleFileUploadProgress((prev) => ({ ...prev, [fileId]: 30 }));

    const reader = new FileReader();
    reader.onloadstart = () => {
      setMultipleFileUploadProgress((prev) => ({ ...prev, [fileId]: 50 }));
    };
    reader.onload = (e) => {
      const base64 = e.target.result;
      setFormData((prev) => ({
        ...prev,
        file: [
          ...prev.file,
          {
            file: base64,
            name: file.name,
            id: fileId,
            size: file.size,
          },
        ],
      }));
      setMultipleFileUploadProgress((prev) => ({ ...prev, [fileId]: 100 }));
      setTimeout(() => {
        setMultipleFileUploadProgress((prev) => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
      }, 500);
    };
    reader.onerror = () => {
      setErrors((prev) => ({
        ...prev,
        multipleFiles: "Gagal mengkonversi file",
      }));
      setMultipleFileUploadProgress((prev) => {
        const newState = { ...prev };
        delete newState[fileId];
        return newState;
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveMultipleFile = (fileId) => {
    setFormData((prev) => ({
      ...prev,
      file: prev.file.filter((f) => f.id !== fileId),
    }));
  };

  const convertToBase64 = (file, type) => {
    if (type === "thumbnail") {
      setIsUploading(true);
      setUploadProgress(30);
    } else {
      setIsUploadingMajalah(true);
      setMajalahUploadProgress(30);
    }

    const reader = new FileReader();
    reader.onloadstart = () => {
      if (type === "thumbnail") setUploadProgress(50);
      else setMajalahUploadProgress(50);
    };
    reader.onload = (e) => {
      const base64 = e.target.result;
      if (type === "thumbnail") {
        setFormData((prev) => ({ ...prev, thumbnail: base64 }));
        setUploadProgress(100);
        setTimeout(() => {
          setIsUploading(false);
          setUploadProgress(0);
        }, 500);
      } else {
        setFormData((prev) => ({ ...prev, file_majalah_base64: base64 }));
        setMajalahUploadProgress(100);
        setTimeout(() => {
          setIsUploadingMajalah(false);
          setMajalahUploadProgress(0);
        }, 500);
      }
    };
    reader.onerror = () => {
      if (type === "thumbnail") {
        setErrors((prev) => ({
          ...prev,
          thumbnail: "Gagal mengkonversi gambar",
        }));
        setIsUploading(false);
        setUploadProgress(0);
      } else {
        setErrors((prev) => ({
          ...prev,
          file_majalah: "Gagal mengkonversi file",
        }));
        setIsUploadingMajalah(false);
        setMajalahUploadProgress(0);
      }
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.judul.trim()) newErrors.judul = "Judul is required";
    if (!formData.ringkasan.trim())
      newErrors.ringkasan = "Ringkasan is required";

    if (contentType === "ARTIKEL") {
      if (!formData.type_artikel)
        newErrors.type_artikel = "Type artikel is required";
      const konten = getQuillContent();
      if (isEmptyContent(konten)) newErrors.konten = "Konten is required";
    } else if (contentType === "COE" || contentType === "FASILITAS_LAYANAN") {
      const konten = getQuillContent();
      if (isEmptyContent(konten)) newErrors.konten = "Konten is required";
    } else if (contentType === "PODCAST") {
      if (!formData.url_youtube.trim()) {
        newErrors.url_youtube = "YouTube URL is required";
      } else if (!isValidYouTubeUrl(formData.url_youtube)) {
        newErrors.url_youtube = "URL YouTube tidak valid";
      }
    } else if (contentType === "MAJALAH") {
      if (!formData.file_majalah_base64 && !initialData?.file_majalah_base64) {
        newErrors.file_majalah = "File majalah is required";
      }
    }

    // Validasi category untuk Fasilitas & Layanan
    if (contentType === "FASILITAS_LAYANAN" && !formData.category) {
      newErrors.category = "Kategori Layanan is required";
    }

    if (formData.thumbnail && formData.thumbnail.length > 10 * 1024 * 1024) {
      newErrors.thumbnail = "Ukuran gambar terlalu besar (max 5MB)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidYouTubeUrl = (url) =>
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/.test(url);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getThumbnailSize = () =>
    formData.thumbnail ? Math.round((formData.thumbnail.length * 3) / 4) : 0;

  const getMajalahFileSize = () => {
    if (formData.file_majalah) return formData.file_majalah.size;
    if (formData.file_majalah_base64)
      return Math.round((formData.file_majalah_base64.length * 3) / 4);
    return 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        type_konten: contentType,
        konten: quillTypes.includes(contentType)
          ? getQuillContent()
          : formData.konten,
        file: formData.file.map(({ file }) => ({ file })),
        category: formData.category, // Ensure category is sent
      };
      await handleSubmit(submitData);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit: error.message || "Gagal menyimpan konten",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const artikelTypeOptions = [
    { value: "TEKNOLOGI", label: "Teknologi" },
    { value: "PENDIDIKAN", label: "Pendidikan" },
    { value: "KESEHATAN", label: "Kesehatan" },
    { value: "BISNIS", label: "Bisnis" },
    { value: "OLAHRAGA", label: "Olahraga" },
    { value: "LAINNYA", label: "Lainnya" },
  ];

  const statusOptions = [
    { value: "DRAFT", label: "Draft" },
    { value: "PUBLISHED", label: "Published" },
    { value: "ARCHIVED", label: "Archived" },
  ];

  const renderContentTypeSelection = () => (
    <div className="text-center py-4">
      <h4 className="mb-4">Pilih Jenis Konten</h4>
      <div className="row g-3">
        <div className="col-md-4">
          <Card
            className="h-100 cursor-pointer hover-shadow"
            onClick={() => handleSelectContentType("ARTIKEL")}
            style={{ cursor: "pointer" }}
          >
            <Card.Body className="text-center py-5">
              <div className="mb-3">
                <div className="bg-primary bg-opacity-10 rounded-circle p-3 d-inline-block">
                  <IoDocumentText size={48} className="text-primary" />
                </div>
              </div>
              <h5>Artikel</h5>
              <p className="text-muted small">
                Buat artikel dengan editor teks lengkap
              </p>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card
            className="h-100 cursor-pointer hover-shadow"
            onClick={() => handleSelectContentType("PODCAST")}
            style={{ cursor: "pointer" }}
          >
            <Card.Body className="text-center py-5">
              <div className="mb-3">
                <div className="bg-danger bg-opacity-10 rounded-circle p-3 d-inline-block">
                  <IoPlayCircle size={48} className="text-danger" />
                </div>
              </div>
              <h5>Podcast</h5>
              <p className="text-muted small">
                Bagikan podcast dengan link YouTube
              </p>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card
            className="h-100 cursor-pointer hover-shadow"
            onClick={() => handleSelectContentType("MAJALAH")}
            style={{ cursor: "pointer" }}
          >
            <Card.Body className="text-center py-5">
              <div className="mb-3">
                <div className="bg-success bg-opacity-10 rounded-circle p-3 d-inline-block">
                  <IoNewspaper size={48} className="text-success" />
                </div>
              </div>
              <h5>Majalah</h5>
              <p className="text-muted small">
                Upload file majalah (PDF/DOC) dengan deskripsi
              </p>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card
            className="h-100 cursor-pointer hover-shadow"
            onClick={() => handleSelectContentType("COE")}
            style={{ cursor: "pointer" }}
          >
            <Card.Body className="text-center py-5">
              <div className="mb-3">
                <div className="bg-info bg-opacity-10 rounded-circle p-3 d-inline-block">
                  <IoPerson size={48} className="text-info" />
                </div>
              </div>
              <h5>COE</h5>
              <p className="text-muted small">Profil dan informasi COE</p>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card
            className="h-100 cursor-pointer hover-shadow"
            onClick={() => handleSelectContentType("FASILITAS_LAYANAN")}
            style={{ cursor: "pointer" }}
          >
            <Card.Body className="text-center py-5">
              <div className="mb-3">
                <div className="bg-warning bg-opacity-10 rounded-circle p-3 d-inline-block">
                  <IoBusiness size={48} className="text-warning" />
                </div>
              </div>
              <h5>Fasilitas & Layanan</h5>
              <p className="text-muted small">
                Informasi fasilitas dan layanan
              </p>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );

  const renderContentForm = () => {
    const isQuillType = quillTypes.includes(contentType);

    return (
      <div className="row g-3">
        {/* Judul */}
        <div className="col-12">
          <FloatingLabel controlId="judul" label="Judul *">
            <Form.Control
              type="text"
              name="judul"
              value={formData.judul}
              onChange={handleChange}
              placeholder="Judul"
              isInvalid={!!errors.judul}
            />
            <Form.Control.Feedback type="invalid">
              {errors.judul}
            </Form.Control.Feedback>
          </FloatingLabel>
        </div>

        {/* Kategori Artikel (hanya ARTIKEL) */}
        {contentType === "ARTIKEL" && (
          <div className="col-md-6">
            <FloatingLabel controlId="type_artikel" label="Kategori Artikel *">
              <Form.Select
                name="type_artikel"
                value={formData.type_artikel}
                onChange={handleChange}
                isInvalid={!!errors.type_artikel}
              >
                <option value="">Pilih kategori</option>
                {artikelTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.type_artikel}
              </Form.Control.Feedback>
            </FloatingLabel>
          </div>
        )}

        {/* Kategori Layanan (Fasilitas & Layanan) */}
        {contentType === "FASILITAS_LAYANAN" && (
          <div className="col-md-6">
            <FloatingLabel controlId="category" label="Kategori Layanan *">
              <Form.Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                isInvalid={!!errors.category}
              >
                <option value="">Pilih kategori</option>
                <option value="LAYANAN">Layanan</option>
                <option value="FASILITAS">Fasilitas</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.category}
              </Form.Control.Feedback>
            </FloatingLabel>
          </div>
        )}

        {/* Status */}
        <div
          className={
            contentType === "ARTIKEL" || contentType === "FASILITAS_LAYANAN"
              ? "col-md-6"
              : "col-12"
          }
        >
          <FloatingLabel controlId="status" label="Status">
            <Form.Select
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </div>

        {/* Ringkasan */}
        <div className="col-12">
          <FloatingLabel controlId="ringkasan" label="Ringkasan *">
            <Form.Control
              as="textarea"
              name="ringkasan"
              value={formData.ringkasan}
              onChange={handleChange}
              placeholder="Ringkasan singkat"
              style={{ height: "100px" }}
              isInvalid={!!errors.ringkasan}
            />
            <Form.Control.Feedback type="invalid">
              {errors.ringkasan}
            </Form.Control.Feedback>
          </FloatingLabel>
        </div>

        {/* Konten untuk tipe Quill */}
        {isQuillType && (
          <div className="col-12">
            <Form.Label className="fw-semibold mb-2">Konten *</Form.Label>
            <div ref={editorRef}></div>
            {errors.konten && (
              <div className="invalid-feedback d-block">{errors.konten}</div>
            )}
            <small className="text-muted">
              Gunakan toolbar di atas untuk memformat konten
            </small>
          </div>
        )}

        {/* Konten untuk PODCAST */}
        {contentType === "PODCAST" && (
          <div className="col-12">
            <FloatingLabel controlId="url_youtube" label="URL YouTube *">
              <Form.Control
                type="url"
                name="url_youtube"
                value={formData.url_youtube}
                onChange={handleChange}
                placeholder="https://www.youtube.com/watch?v=..."
                isInvalid={!!errors.url_youtube}
              />
              <Form.Control.Feedback type="invalid">
                {errors.url_youtube}
              </Form.Control.Feedback>
            </FloatingLabel>
            <small className="text-muted">
              Masukkan link YouTube podcast Anda
            </small>
          </div>
        )}

        {/* Konten untuk MAJALAH */}
        {contentType === "MAJALAH" && (
          <div className="col-12">
            <Form.Label className="fw-semibold mb-2">
              File Majalah *{" "}
              <small className="text-muted">
                (PDF atau DOC/DOCX, max 20MB)
              </small>
            </Form.Label>
            <input
              type="file"
              ref={majalahFileInputRef}
              onChange={handleMajalahFileSelect}
              accept=".pdf,.doc,.docx"
              className="d-none"
            />
            {!formData.file_majalah_base64 && !formData.file_majalah ? (
              <Card
                className="border-dashed text-center cursor-pointer"
                onClick={() => majalahFileInputRef.current?.click()}
                style={{
                  border: "2px dashed #dee2e6",
                  background: "#f8f9fa",
                  cursor: "pointer",
                  minHeight: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Card.Body className="py-4">
                  <IoCloudUpload size={36} className="text-muted mb-2" />
                  <p className="mb-1">Klik untuk upload file majalah</p>
                  <p className="text-muted small mb-0">
                    Format: PDF, DOC, DOCX (max 20MB)
                  </p>
                </Card.Body>
              </Card>
            ) : (
              <Card>
                <Card.Body>
                  <Row className="align-items-center">
                    <Col xs={2}>
                      <div className="bg-light rounded p-2 text-center">
                        <IoDocumentText size={24} className="text-primary" />
                      </div>
                    </Col>
                    <Col xs={8}>
                      <div>
                        <strong className="d-block">
                          {formData.file_majalah?.name || "File Majalah"}
                        </strong>
                        <small className="text-muted">
                          {formatFileSize(getMajalahFileSize())}
                        </small>
                      </div>
                    </Col>
                    <Col xs={2} className="text-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveMajalahFile}
                        disabled={isUploadingMajalah}
                      >
                        <IoTrash />
                      </Button>
                    </Col>
                  </Row>
                  {isUploadingMajalah && (
                    <ProgressBar
                      now={majalahUploadProgress}
                      label={`${majalahUploadProgress}%`}
                      animated
                      className="mt-3"
                    />
                  )}
                </Card.Body>
              </Card>
            )}
            {errors.file_majalah && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.file_majalah}
              </Form.Control.Feedback>
            )}
          </div>
        )}

        {/* Multiple Files - ONLY for types that support them */}
        {fileSupportTypes.includes(contentType) && (
          <div className="col-12">
            <Form.Label className="fw-semibold mb-2">
              File Tambahan{" "}
              <small className="text-muted">(Opsional, bisa multiple)</small>
            </Form.Label>
            <input
              type="file"
              ref={multipleFileInputRef}
              onChange={handleMultipleFilesSelect}
              multiple
              accept=".pdf,.doc,.docx,image/*"
              className="d-none"
            />
            <Card
              className="border-dashed text-center cursor-pointer"
              onClick={() => multipleFileInputRef.current?.click()}
              style={{
                border: "2px dashed #dee2e6",
                background: "#f8f9fa",
                cursor: "pointer",
                minHeight: "100px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Card.Body className="py-3">
                <IoAdd size={24} className="text-muted mb-1" />
                <p className="mb-0">
                  Klik untuk tambah file (PDF, Word, Gambar)
                </p>
                <small className="text-muted">Maksimal 1MB per file</small>
              </Card.Body>
            </Card>
            {formData.file.length > 0 && (
              <div className="mt-3">
                <strong>File yang dipilih:</strong>
                <div className="mt-2">
                  {formData.file.map((fileItem) => (
                    <Card key={fileItem.id} className="mb-2">
                      <Card.Body className="py-2">
                        <Row className="align-items-center">
                          <Col xs={2}>
                            <div className="bg-light rounded p-2 text-center">
                              <IoDocumentText
                                size={24}
                                className="text-primary"
                              />
                            </div>
                          </Col>
                          <Col xs={8}>
                            <div>
                              <strong className="d-block">
                                {fileItem.name}
                              </strong>
                              <small className="text-muted">
                                {formatFileSize(fileItem.size)}
                              </small>
                            </div>
                            {multipleFileUploadProgress[fileItem.id] && (
                              <ProgressBar
                                now={multipleFileUploadProgress[fileItem.id]}
                                label={`${multipleFileUploadProgress[fileItem.id]}%`}
                                animated
                                className="mt-2"
                              />
                            )}
                          </Col>
                          <Col xs={2} className="text-end">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() =>
                                handleRemoveMultipleFile(fileItem.id)
                              }
                              disabled={
                                !!multipleFileUploadProgress[fileItem.id]
                              }
                            >
                              <IoTrash />
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            {errors.multipleFiles && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.multipleFiles}
              </Form.Control.Feedback>
            )}
          </div>
        )}

        {/* Thumbnail */}
        <div className="col-12">
          <Form.Label className="fw-semibold mb-2">
            Thumbnail <small className="text-muted">(Opsional)</small>
          </Form.Label>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleThumbnailSelect}
            accept="image/*"
            className="d-none"
          />
          {!formData.thumbnail ? (
            <Card
              className="border-dashed text-center cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: "2px dashed #dee2e6",
                background: "#f8f9fa",
                cursor: "pointer",
                minHeight: "150px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Card.Body className="py-4">
                <IoCloudUpload size={36} className="text-muted mb-2" />
                <p className="mb-1">Klik untuk upload thumbnail</p>
                <p className="text-muted small mb-0">
                  Format: JPG, PNG, GIF, WebP (max 5MB)
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Card>
              <Card.Body>
                <Row className="align-items-center">
                  <Col xs={3}>
                    <div className="position-relative">
                      <img
                        src={formData.thumbnail}
                        alt="Thumbnail preview"
                        className="img-fluid rounded"
                        style={{
                          width: "100%",
                          height: "100px",
                          objectFit: "cover",
                        }}
                      />
                      {isUploading && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex align-items-center justify-content-center rounded">
                          <div className="text-center">
                            <div
                              className="spinner-border spinner-border-sm text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <small className="d-block mt-1">
                              Processing...
                            </small>
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div>
                      <strong className="d-block">
                        {formData.thumbnailFile?.name || "Thumbnail"}
                      </strong>
                      <small className="text-muted">
                        {formData.thumbnailFile
                          ? formatFileSize(formData.thumbnailFile.size)
                          : formatFileSize(getThumbnailSize())}
                      </small>
                    </div>
                  </Col>
                  <Col xs={3} className="text-end">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleRemoveThumbnail}
                      disabled={isUploading}
                    >
                      <IoTrash />
                    </Button>
                  </Col>
                </Row>
                {isUploading && (
                  <ProgressBar
                    now={uploadProgress}
                    label={`${uploadProgress}%`}
                    animated
                    className="mt-3"
                  />
                )}
              </Card.Body>
            </Card>
          )}
          {errors.thumbnail && (
            <Form.Control.Feedback type="invalid" className="d-block">
              {errors.thumbnail}
            </Form.Control.Feedback>
          )}
        </div>

        {/* Checkboxes */}
        <div className="col-md-6">
          <Form.Check
            type="checkbox"
            id="allowComments"
            name="allowComments"
            label="Allow Comments"
            checked={formData.allowComments}
            onChange={handleChange}
            className="mt-2"
          />
        </div>
        <div className="col-md-6">
          <Form.Check
            type="checkbox"
            id="isFeatured"
            name="isFeatured"
            label="Featured"
            checked={formData.isFeatured}
            onChange={handleChange}
            className="mt-2"
          />
        </div>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {initialData ? "Edit Konten" : "Tambah Konten Baru"}
          {contentType && (
            <small className="ms-2 text-muted">
              (
              {contentType === "ARTIKEL"
                ? "Artikel"
                : contentType === "PODCAST"
                  ? "Podcast"
                  : contentType === "MAJALAH"
                    ? "Majalah"
                    : contentType === "COE"
                      ? "COE"
                      : "Fasilitas & Layanan"}
              )
            </small>
          )}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleFormSubmit}>
        <Modal.Body>
          {errors.submit && (
            <Alert variant="danger" className="py-2">
              {errors.submit}
            </Alert>
          )}

          {!initialData && contentType && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleBackToTypeSelection}
              className="mb-3"
            >
              <IoArrowBack className="me-1" /> Pilih Tipe Lain
            </Button>
          )}

          {!contentType && !initialData
            ? renderContentTypeSelection()
            : renderContentForm()}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting || isUploading || isUploadingMajalah}
          >
            <IoClose /> Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={
              isSubmitting ||
              isUploading ||
              isUploadingMajalah ||
              (!contentType && !initialData)
            }
          >
            {isSubmitting ? "Menyimpan..." : initialData ? "Update" : "Simpan"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ArtikelModal;
